import React, { FC, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { type ContentData, DataGaEventPrefix } from './types';

import { Theme, useTheme } from 'app/lib/theme';
import { SigningModalsQuery } from 'app/modules/Signing/utils/signing-modals-query';
import { AuthModalQuery } from 'app/modules/SigningNew/utils/auth-modals-query';
import InputWithAnalytics from 'app/widgets/InputsWithAnalytics/Input';

import CodeResendInfo from './CodeResendInfo';
import { CODE_LENGTH, CodeError, FIELD_NAME } from './constants';

import styles from './CodeConfirmation.module.scss';

type ContentProps = {
  data: ContentData;
};

const Content: FC<ContentProps> = ({ data }) => {
  const { search } = useLocation();
  const { inputDataEvent, resentDataEvent, changeDataEvent } = getDataGaEventBySearchParams(search);
  const { changeVerifyData, codeResendInfo, error, touched } = data;
  const { theme } = useTheme();
  const fieldError = error !== null && error !== CodeError.NOT_SEND;

  const promptClassNames = classNames(styles.fieldInfo, {
    [styles.error]: fieldError,
  });

  const confirmCodeInputClassNames = classNames(styles.confirmCodeInput, {
    [styles.dark]: theme === Theme.DARK,
    [styles.light]: theme === Theme.LIGHT,
  });

  const handleChangeDataButton = useCallback(() => {
    changeVerifyData();
  }, [changeVerifyData]);

  const ref = React.useRef() as React.MutableRefObject<HTMLInputElement>;

  useEffect(() => {
    if (touched && ref.current) {
      ref.current.blur();
    }
  }, [touched, ref.current]);

  return (
    <>
      <span className={styles.fieldInfoSendCode}>{data.title}</span>
      <InputWithAnalytics name={FIELD_NAME}
                          type="text"
                          ref={ref}
                          maxLength={CODE_LENGTH}
                          value={data.value}
                          placeholder=" "
                          error={fieldError}
                          onChange={data.onChange}
                          autoFocus
                          onFocus={data.onFocus}
                          disabled={data.disabled}
                          className={confirmCodeInputClassNames}
                          data-ga-event={inputDataEvent}
                          data-test="Modal-codeConfirmation-input" />
      <span className={promptClassNames}
            data-test="code-prompt">{data.prompt}
      </span>
      <div className={styles.repeatCode}>
        <CodeResendInfo resendDate={codeResendInfo.resendDate}
                        serverTime={codeResendInfo.serverTime}
                        onChange={codeResendInfo.onChange}
                        handler={codeResendInfo.handler}
                        dataGaEvent={resentDataEvent} />
      </div>
      <button className={styles.changeDataButton}
              type="button"
              onClick={handleChangeDataButton}
              data-ga-event={changeDataEvent}
              data-test="change-data-button">
        {data.changeValue}
      </button>
    </>
  );
};
function getDataGaEventBySearchParams(search: string) {
  if (search.includes(SigningModalsQuery.RESTORE_PASSWORD)) {
    return getEvents(DataGaEventPrefix.PASSWORD_RESET);
  }

  if (search.includes(SigningModalsQuery.REGISTRATION
    || search.includes(AuthModalQuery.SIGN))) {
    return getEvents(DataGaEventPrefix.SIGN_IN);
  }

  return getEvents();
}

function getEvents(prefix?: DataGaEventPrefix) {
  if (!prefix) {
    return {};
  }

  return {
    inputDataEvent: `${prefix}CodeInput`,
    resentDataEvent: `${prefix}CodeResent`,
    changeDataEvent: `${prefix}NumberChange`,
  };
}

export default Content;
